import { memo } from 'react';
import { Handle, type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, tokens } from '@coreai/fluentui-react';
import { useHandlePositions } from './useHandlePositions';

export type MergeNodeData = {
  /** The condition/switch node this merge belongs to */
  parentActionName: string;
};

export type MergeNodeType = Node<MergeNodeData, 'merge'>;

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Fade in with slight delay (appears after branch nodes)
    animationName: {
      from: { opacity: 0, transform: 'scale(0.9)' },
      to: { opacity: 1, transform: 'scale(1)' },
    },
    animationDuration: tokens.durationNormal,
    animationTimingFunction: tokens.curveDecelerateMax,
    animationFillMode: 'backwards',
    animationDelay: tokens.durationNormal,
    '@media (prefers-reduced-motion: reduce)': {
      animationDuration: '0.01ms',
      animationDelay: '0ms',
    },
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralStroke1,
    border: `1.5px solid ${tokens.colorNeutralBackground1}`,
    boxShadow: tokens.shadow2,
  },
});

/**
 * Tiny merge indicator node — marks where condition branches converge
 * back into a single flow. Renders as a small neutral dot.
 */
export const MergeNode = memo(({ data }: NodeProps<MergeNodeType>) => {
  const styles = useStyles();
  const handlePositions = useHandlePositions();

  return (
    <div
      className={styles.wrapper}
      role="img"
      aria-label={`Merge point for ${data.parentActionName} condition`}
    >
      <Handle
        type="target"
        position={handlePositions.input}
        id="input"
        isConnectable={false}
        style={{ opacity: 0, width: 1, height: 1 }}
      />
      <div className={styles.dot} />
      <Handle
        type="source"
        position={handlePositions.output}
        id="output"
        isConnectable={false}
        style={{ opacity: 0, width: 1, height: 1 }}
      />
    </div>
  );
});

MergeNode.displayName = 'MergeNode';
